/**
 * Journey — the unified progress dashboard.
 *
 * Replaces the separate Today / Progress / Journey tabs with one
 * information-dense page organized top-down from "what's your status"
 * → "how are you doing" → "where are the gaps" → "what did you learn".
 * Every section answers a user question, every viz is picked to suit
 * its metric (numbers for scalars, bars for rankings, lines for
 * time-series, stacked bars for per-day composition, etc).
 *
 * Admins can view any user's journey via ?uid=.
 */
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { api, type ProgressHistory } from "../../api";
import { useProfile } from "../../layout/AppShell";
import { cn } from "../../lib/format";
import LanguageTabs from "../../components/LanguageTabs";
import TimeSeriesLine from "../../components/charts/TimeSeriesLine";
import MasteryStackedBars from "../../components/charts/MasteryStackedBars";
import CefrStepLine from "../../components/charts/CefrStepLine";
import CefrLevelGrid from "../../components/charts/CefrLevelGrid";
import CEFRLadder from "../../components/charts/CEFRLadder";
import PosDistribution from "../../components/charts/PosDistribution";
import GrammarErrorRanking from "../../components/charts/GrammarErrorRanking";
import OrphanWordsPanel from "../../components/charts/OrphanWordsPanel";
import RecentMasteredChips from "../../components/charts/RecentMasteredChips";
import AgentActivity from "../../components/charts/AgentActivity";
import { fadeUp, stagger } from "../../lib/motion";

const WINDOW_OPTIONS = [
  { label: "30 days", value: 30 },
  { label: "60 days", value: 60 },
  { label: "90 days", value: 90 },
];

const BOT_URL = `https://t.me/${import.meta.env.VITE_BOT_USERNAME || "ilmli_bot"}`;

function languageDisplayName(code: string): string {
  const map: Record<string, string> = {
    de: "German", es: "Spanish", fr: "French", it: "Italian",
    pt: "Portuguese", el: "Greek", pl: "Polish",
  };
  return map[code?.toLowerCase()] || (code || "").toUpperCase();
}

function Stat({ label, value, sub }: { label: string; value: string | number; sub: string }) {
  return (
    <div className="border-l border-[rgba(42,36,26,0.15)] pl-4">
      <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-ink-faint">{label}</div>
      <div className="mt-1 font-serif text-3xl">{value}</div>
      <div className="mt-1 font-serif italic text-[12px] text-ink-soft">{sub}</div>
    </div>
  );
}

type SeriesSnapshot = {
  seen: number[];
  learning: number[];
  active: number[];
  engagement: number[];
  native: number[];
  userNative: number[] | null;
};

function NarrativeSummary({
  history,
  series,
  languageName,
}: {
  history: ProgressHistory | null;
  series: SeriesSnapshot | null;
  languageName: string;
}) {
  if (!history || !series || series.seen.length === 0) return null;

  // Words gained over the last 7 days (cumulative seen delta).
  const seen = series.seen;
  const tail = seen.length;
  const head = Math.max(0, tail - 7);
  const grew = Math.max(0, (seen[tail - 1] || 0) - (seen[head] || 0));

  // Engagement rate: average over the last 7 days.
  const eng = series.engagement.slice(-7).filter((v) => v > 0);
  const engagement = eng.length ? Math.round(eng.reduce((a, b) => a + b, 0) / eng.length) : null;

  const dueNow = history.current_snapshot?.due_now ?? 0;
  const cefr = history.current_cefr;

  return (
    <motion.div variants={fadeUp} className="mt-6 border-l-[3px] border-terracotta pl-5 max-w-3xl">
      <p className="font-serif text-xl md:text-[1.4rem] leading-snug">
        {grew > 0 ? "This week your " : "You're learning "}
        <span className="italic-accent">{languageName}</span>
        {grew > 0 ? (
          <>
            {" "}grew by{" "}
            <span className="font-medium not-italic">{grew} {grew === 1 ? "word" : "words"}</span>
            {engagement !== null && (
              <>
                {" "}and you replied to{" "}
                <span className="font-medium not-italic">{engagement}%</span>
                {" "}of your tutor's pings
              </>
            )}.
          </>
        ) : (
          <>
            . You're at <span className="font-medium not-italic">{cefr}</span>.
          </>
        )}
        {cefr && grew > 0 && (
          <> You're at <span className="font-medium not-italic">{cefr}</span>.</>
        )}
      </p>
      {dueNow > 0 && (
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <span className="font-mono text-xs text-ink-faint">
            {dueNow} {dueNow === 1 ? "word is" : "words are"} due —
          </span>
          <a
            href={BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs text-terracotta underline decoration-terracotta/40 hover:decoration-terracotta"
          >
            review in Telegram →
          </a>
        </div>
      )}
    </motion.div>
  );
}

export default function Journey() {
  const { profile, setProfile } = useProfile();
  const [searchParams] = useSearchParams();
  const adminViewingUid = searchParams.get("uid");

  const [history, setHistory] = useState<ProgressHistory | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [viewLang, setViewLang] = useState<string>(profile.active_language);
  const [days, setDays] = useState<number>(30);

  useEffect(() => {
    setHistory(null);
    setErr(null);
    api
      .getProgressHistory({
        lang: viewLang,
        days,
        uid: adminViewingUid || undefined,
      })
      .then(setHistory)
      .catch((e) => setErr(e instanceof Error ? e.message : "load failed"));
  }, [viewLang, days, adminViewingUid]);

  const owned = profile.languages || [profile.active_language];

  // Drop leading empty days from the historical series so the first
  // render doesn't show 27 flat-zero days plus a spike on day 28.
  const activeRows = useMemo(() => {
    if (!history) return [];
    const rows = history.daily;
    const firstActive = rows.findIndex(
      (r) => (Number(r.seen) || 0) > 0 || (Number(r.turns_count) || 0) > 0,
    );
    return firstActive >= 0 ? rows.slice(firstActive) : rows;
  }, [history]);

  const series = useMemo(() => {
    if (!history) return null;
    // user_native_ratio was added later than native_ratio. Older
    // DAILY_STATS rows don't have it, and `Number(undefined) || 0` would
    // render those days as 0% (= "100% target language"), which is
    // misleadingly wrong. Only build the user-line if at least one day
    // in the window actually has the field set.
    const userHasData = activeRows.some(
      (r) => typeof r.user_native_ratio === "number",
    );
    return {
      seen: activeRows.map((r) => Number(r.seen) || 0),
      learning: activeRows.map((r) => Number(r.learning) || 0),
      active: activeRows.map((r) => Number(r.active) || 0),
      engagement: activeRows.map((r) => (Number(r.engagement_rate) || 0) * 100),
      native: activeRows.map((r) => (Number(r.native_ratio) || 0) * 100),
      userNative: userHasData
        ? activeRows.map((r) => (Number(r.user_native_ratio) || 0) * 100)
        : null,
    };
  }, [history, activeRows]);

  const snap = history?.current_snapshot;

  return (
    <motion.div initial="hidden" animate="show" variants={stagger(0.06)}>
      {adminViewingUid && adminViewingUid !== profile.sub ? (
        <motion.div
          variants={fadeUp}
          className="mb-4 px-4 py-3 rounded-md bg-terracotta/10 border border-terracotta/30 text-sm text-terracotta-dark"
        >
          👁 Admin view — showing journey for user{" "}
          <code className="font-mono text-xs">{adminViewingUid}</code>
        </motion.div>
      ) : null}

      {/* --- HERO --- */}
      <motion.div variants={fadeUp}>
        <h1 className="font-serif text-3xl md:text-4xl leading-tight">
          Journey · {languageDisplayName(profile.active_language)}
        </h1>
      </motion.div>

      {/* --- NARRATIVE SUMMARY (week-on-week, in plain English) --- */}
      <NarrativeSummary
        history={history}
        series={series}
        languageName={languageDisplayName(viewLang)}
      />

      <motion.div variants={fadeUp} className="mt-4 flex flex-wrap items-center gap-4">
        <LanguageTabs
          languages={owned}
          value={viewLang}
          active={profile.active_language}
          onChange={setViewLang}
          onMakeActive={async (code) => {
            const updated = await api.updateMe({ active_language: code });
            setProfile(updated);
          }}
        />
        <span className="font-mono text-[12px] text-ink-faint">
          last{" "}
          {WINDOW_OPTIONS.map((opt, i) => {
            const isActive = days === opt.value;
            return (
              <span key={opt.value}>
                {i > 0 && <span className="mx-1.5 text-ink-faint/50">·</span>}
                <button
                  onClick={() => setDays(opt.value)}
                  className={cn(
                    "transition-colors inline-block py-2 -my-2",
                    isActive
                      ? "text-ink underline decoration-ink underline-offset-2 font-medium"
                      : "text-ink-faint underline decoration-ink-faint/40 hover:text-ink hover:decoration-ink",
                  )}
                >
                  {opt.value} days
                </button>
              </span>
            );
          })}
        </span>
      </motion.div>

      {err ? (
        <motion.div variants={fadeUp} className="mt-8 text-terracotta">{err}</motion.div>
      ) : !history || !series ? (
        <motion.div variants={fadeUp} className="mt-8">
          <div className="font-serif italic text-ink-soft text-base">
            Reading the last {days} days…
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-24 rounded-md bg-paper-soft/60 animate-pulse"
                style={{ animationDelay: `${i * 100}ms` }}
              />
            ))}
          </div>
        </motion.div>
      ) : activeRows.length === 0 && !snap ? (
        <motion.div variants={fadeUp} className="mt-10 border-t border-[rgba(42,36,26,0.12)] pt-10 max-w-2xl">
          <h2 className="font-serif text-2xl">Nothing to chart yet.</h2>
          <p className="mt-3 font-serif italic text-ink-soft">
            Once your tutor and you trade a few messages, this page fills in —
            CEFR climb, words seen, follow-ups, the small Sunday note. None of
            it happens until you say hello.
          </p>
          <a
            href={BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 font-mono text-xs text-terracotta underline decoration-terracotta/40 hover:decoration-terracotta"
          >
            Open Telegram →
          </a>
        </motion.div>
      ) : (
        <>
          {/* --- CEFR LADDER (spine) — single horizontal pill row --- */}
          {history.level_breakdowns && history.level_breakdowns.length > 0 ? (
            <motion.div variants={fadeUp} className="mt-8">
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-faint mb-3">
                your ladder
              </div>
              <CEFRLadder
                breakdowns={history.level_breakdowns}
                currentLevel={history.current_cefr}
              />
            </motion.div>
          ) : null}

          {/* --- HERO CHART: vocabulary over time --- */}
          <motion.div variants={fadeUp} className="mt-8">
            <TimeSeriesLine
              title="Vocabulary, over time"
              yLabel="words"
              series={[
                { label: "seen", values: series.seen, color: "#6d6a63" },
                { label: "learning", values: series.learning, color: "#c88649" },
                { label: "mastered", values: series.active, color: "#3f6a44" },
              ]}
            />
          </motion.div>

          {/* --- SMALL MULTIPLES: same x-axis, two engagement charts --- */}
          <motion.div variants={fadeUp} className="mt-6">
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-faint mb-2">
              small multiples · same {days}-day window
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TimeSeriesLine
                title="Engagement"
                yLabel="% of pings replied to"
                yMax={100}
                yFormat={(v) => `${v.toFixed(0)}%`}
                series={[
                  { label: "engagement", values: series.engagement, color: "#b9523a" },
                ]}
              />
              <TimeSeriesLine
                title="Native-language ratio — tutor vs. you"
                yLabel="% native (lower = more target language)"
                yMax={100}
                yFormat={(v) => `${v.toFixed(0)}%`}
                series={[
                  { label: "tutor", values: series.native, color: "#7b9458" },
                  ...(series.userNative
                    ? [{ label: "you", values: series.userNative, color: "#b9523a" }]
                    : []),
                ]}
              />
            </div>
          </motion.div>

          {/* --- CEFR PROGRESSION + MASTERY MIX in one row --- */}
          <motion.div variants={fadeUp} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <CefrStepLine
              rows={activeRows}
              events={history.cefr_events}
              currentLevel={history.current_cefr}
            />
            <MasteryStackedBars title="Mastery mix" rows={activeRows} />
          </motion.div>

          {/* --- DEEP DIVE: detail panels, demoted below the spine --- */}
          {snap ? (
            <details className="mt-12 group">
              <summary className="cursor-pointer list-none flex items-baseline gap-3">
                <span className="font-serif text-[1.5rem]">Deep dive</span>
                <span className="font-mono text-[10px] tracking-wide text-ink-faint group-open:hidden">
                  ▸ open
                </span>
                <span className="font-mono text-[10px] tracking-wide text-ink-faint hidden group-open:inline">
                  ▾ collapse
                </span>
              </summary>

              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="CEFR level" value={history.current_cefr} sub={`${snap.cefr_vocab_active}/${snap.cefr_vocab_total} ${history.current_cefr} words`} />
                <Stat label="mastered" value={snap.active} sub="words used unaided" />
                <Stat label="learning" value={snap.learning} sub="still in rotation" />
                <Stat label="due today" value={snap.due_now} sub={snap.due_now === 0 ? "all caught up" : "ready to review"} />
              </div>

              {/* Detailed per-level breakdown — kept available below the spine */}
              {history.level_breakdowns && history.level_breakdowns.length > 0 && (
                <div className="mt-6">
                  <CefrLevelGrid
                    breakdowns={history.level_breakdowns}
                    currentLevel={history.current_cefr}
                  />
                </div>
              )}

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                <PosDistribution distribution={snap.pos_distribution} />
                <OrphanWordsPanel
                  count={snap.orphan_count}
                  sample={snap.orphan_sample}
                />
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                <GrammarErrorRanking
                  errors={snap.top_grammar_errors}
                  mastery={snap.grammar_mastery}
                />
                <RecentMasteredChips words={snap.recent_mastered} />
              </div>

              <div className="mt-6">
                <AgentActivity events={history.agent_events} />
              </div>
            </details>
          ) : null}
        </>
      )}
    </motion.div>
  );
}
